// src/hooks/usePagination.ts

import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'src/redux/index'
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalItems,
} from 'src/redux/Components/Pagination/PaginationSlice'

/**
 * Custom hook for managing pagination state and behavior in a Redux store.
 * It provides access to the current page, number of rows per page, total items,
 * and functions to update these values.
 *
 * @returns An object containing the current pagination state and methods
 *          to update pagination settings.
 */
export const usePagination = () => {
  const dispatch = useDispatch()
  const { currentPage, rowsPerPage, rowsPerPageOptions, totalItems } =
    useSelector((state: RootState) => state.pagination)

  /**
   * Handles the event when the page is changed.
   * Updates the current page in the Redux store.
   *
   * @param event - The event triggered by the page change.
   * @param newPage - The new page number to set.
   */
  const handlePageChange = (event: unknown, newPage: number) => {
    if (newPage <= 0) {
      dispatch(setCurrentPage(0))
      return
    }
    dispatch(setCurrentPage(newPage))
  }

  /**
   * Handles the change of the number of rows per page.
   * Updates the rows per page in the Redux store.
   *
   * @param event - The event triggered by the rows per page change.
   */
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)))
  }

  /**
   * Sets the total number of items for pagination.
   *
   * @param total - The total number of items.
   */
  const handleTotalItems = (total: number) => {
    dispatch(setTotalItems(total ?? 0))
  }

  return {
    currentPage,
    rowsPerPage,
    rowsPerPageOptions,
    totalItems,
    handleTotalItems,
    handlePageChange,
    handleRowsPerPageChange,
  }
}
